import { onBeforeUnmount, onMounted } from "vue";
import { useRouter } from "vue-router";

import { api, ApiError } from "@/api/client";
import { useAuthStore } from "@/stores/auth";

const INTERVAL = 60_000;
const REQUEST_TIMEOUT = 15_000;

/** Visible app usage, including reading a terminal, refreshes the idle window.
 * WebSocket traffic does not go through the server's HTTP auth extractor.
 * The server still enforces absolute expiry and revoked/disabled accounts.
 */
export function useSessionKeepalive() {
  const auth = useAuthStore();
  const router = useRouter();
  let timer: ReturnType<typeof setInterval> | undefined;
  let pending: AbortController | null = null;
  let lastAttempt = Date.now();
  let disposed = false;

  async function refresh() {
    if (
      disposed ||
      !auth.isAuthenticated() ||
      document.visibilityState !== "visible"
    )
      return;

    // Mobile browsers can freeze both fetch and its timeout in the background.
    // Discard an overdue attempt when execution resumes. navigator.onLine is
    // only a network hint; an actual response decides whether renewal works.
    const now = Date.now();
    if (pending && now - lastAttempt >= REQUEST_TIMEOUT) {
      pending.abort();
      pending = null;
    }
    if (pending || now - lastAttempt < INTERVAL) return;

    // Keep the identity of this login. A delayed 401 from an older login
    // must never discard a newer successful login (even for the same user).
    const login = auth.state;
    const controller = new AbortController();
    pending = controller;
    lastAttempt = Date.now();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
    try {
      await api.me(controller.signal);
    } catch (err) {
      if (
        !disposed &&
        !controller.signal.aborted &&
        pending === controller &&
        auth.state === login &&
        err instanceof ApiError &&
        err.status === 401
      ) {
        auth.markAnonymous();
        const current = router.currentRoute.value;
        if (current.name !== "login") {
          void router.replace({ name: "login", query: { redirect: current.fullPath } });
        }
      }
      // Offline, aborted requests and 5xx do not mean the login was lost.
      // Retry on the next interval while leaving the terminal in place.
    } finally {
      clearTimeout(timeout);
      if (pending === controller) pending = null;
    }
  }

  onMounted(() => {
    timer = setInterval(() => void refresh(), INTERVAL);
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("pageshow", refresh);
    window.addEventListener("online", refresh);
  });

  onBeforeUnmount(() => {
    disposed = true;
    clearInterval(timer);
    pending?.abort();
    document.removeEventListener("visibilitychange", refresh);
    window.removeEventListener("pageshow", refresh);
    window.removeEventListener("online", refresh);
  });
}
